import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Input, Spin, Alert } from 'antd';
import {
  ArrowRightOutlined,
  EyeOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import useProjectId from '@hooks/useProjectId';
import { useAiChat } from './AiChatContext';
import MarkdownContent from './MarkdownContent';
import AiChatTooltip from './AiChatTooltip';
import { useThinkingEscalation } from './hooks/useThinkingEscalation';
import AiChatConfigPane from './AiChatConfigPane';
import BotFillIcon from './BotFillIcon';
import BotOutlineIcon from './BotOutlineIcon';
import SettingOutlineIcon from './SettingOutlineIcon';
import OpenInNewOutlineIcon from './OpenInNewOutlineIcon';
import ExpandOutlineIcon from './ExpandOutlineIcon';
import MinimizeOutlineIcon from './MinimizeOutlineIcon';
import CloseOutlineIcon from './CloseOutlineIcon';
import CopyOutlineIcon from './CopyOutlineIcon';
import { copyMarkdown, copySmartFromRendered } from './clipboardRichText';
import { looksLikeMarkdown } from './looksLikeMarkdown';
import { fetchChatbotConfig } from './api/fetchChatbotConfig';
import { getAiChatApiBase, showAiChatBetaChip } from './config';
import { resolveWelcomeAgentDisplayName } from './resolveWelcomeAgentDisplayName';
import { applyIntroMessagePlaceholders } from './applyIntroMessagePlaceholders';
import { getConnectionSettings } from './persistence/connectionSettings';

const DEFAULT_PANEL_WIDTH_PX = 560;
/** Cleared on logout via clearLocalStorage; survives F5 in the same tab. */
const PANEL_WIDTH_SESSION_STORAGE_PREFIX = 'ddvAiChatPanelWidthPx:';
const MIN_PANEL_WIDTH_PX = 300;
const MAX_PANEL_WIDTH_PX = 720;
/** Minimum horizontal space to leave for the main app column when the chat is docked. */
const MAIN_MIN_WIDTH_PX = 480;
/** Above this width the docked chat overlays the main layout (no app-content inset). Legacy fixed width before resizable pane. */
const CHAT_SHRINK_LAYOUT_MAX_WIDTH_PX = 400;

/**
 * Max width so the docked panel + a reasonable main column never exceed the viewport.
 * @returns {number}
 */
function getMaxPanelWidthForViewport() {
  if (typeof window === 'undefined') {
    return MAX_PANEL_WIDTH_PX;
  }
  const w = window.innerWidth;
  const minMain = Math.min(
    MAIN_MIN_WIDTH_PX,
    Math.max(280, Math.floor(w * 0.35)),
  );
  const available = w - minMain;
  return Math.min(MAX_PANEL_WIDTH_PX, Math.max(MIN_PANEL_WIDTH_PX, available));
}

/**
 * @param {number} w
 * @returns {number}
 */
function clampPanelWidthToViewport(w) {
  const n = Math.round(w);
  return Math.min(
    getMaxPanelWidthForViewport(),
    Math.max(MIN_PANEL_WIDTH_PX, n),
  );
}

/**
 * @param {unknown} projectId
 * @returns {string | null}
 */
function panelWidthSessionStorageKey(projectId) {
  const id = projectId && String(projectId).trim();
  if (!id) {
    return null;
  }
  return `${PANEL_WIDTH_SESSION_STORAGE_PREFIX}${id}`;
}

/**
 * @param {unknown} projectId
 * @returns {number | null} clamped width or null if missing/invalid
 */
function readStoredPanelWidthPx(projectId) {
  if (typeof window === 'undefined') {
    return null;
  }
  const key = panelWidthSessionStorageKey(projectId);
  if (!key) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null || raw === '') {
      return null;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) {
      return null;
    }
    return clampPanelWidthToViewport(n);
  } catch {
    return null;
  }
}

/**
 * @param {unknown} projectId
 * @param {number} clampedWidth
 */
function writeStoredPanelWidthPx(projectId, clampedWidth) {
  if (typeof window === 'undefined') {
    return;
  }
  const key = panelWidthSessionStorageKey(projectId);
  if (!key) {
    return;
  }
  try {
    window.sessionStorage.setItem(key, String(clampedWidth));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Apply docked width + shrink/overlay to `documentElement` without React.
 * Used during pointer-drag so we do not re-render the tree every move (avoids
 * ResizeObserver "loop" errors when main layout toggles padding).
 * @param {number} widthPx
 * @returns {number} clamped width applied
 */
function syncDockedChatLayoutDom(widthPx) {
  if (typeof document === 'undefined') {
    return clampPanelWidthToViewport(widthPx);
  }
  const w = clampPanelWidthToViewport(widthPx);
  document.documentElement.style.setProperty(
    '--ddv-ai-chat-panel-width',
    `${w}px`,
  );
  document.documentElement.setAttribute(
    'data-ai-chat-docked-layout',
    w > CHAT_SHRINK_LAYOUT_MAX_WIDTH_PX ? 'overlay' : 'shrink',
  );
  return w;
}

const COPY_FEEDBACK_MS = 2000;

/** Avoid breaking markdown emphasis if display names contain `*`. */
function escapeMarkdownAsterisks(s) {
  return String(s).replace(/\*/g, '\\*');
}

/**
 * @param {{
 *   message: { id: string, content: string, streaming?: boolean },
 *   emptyStreamingHint?: string,
 *   showCopy?: boolean,
 *   dimCopyUntilHover?: boolean,
 * }} props
 */
function AssistantMessageBubble({
  message,
  emptyStreamingHint,
  showCopy = true,
  dimCopyUntilHover = false,
}) {
  const renderedRootRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  const canCopy =
    !message.streaming && message.content && String(message.content).trim();

  useEffect(
    () => () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    },
    [],
  );

  const flashCopied = () => {
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
    }
    setCopied(true);
    copyTimerRef.current = setTimeout(() => {
      setCopied(false);
      copyTimerRef.current = null;
    }, COPY_FEEDBACK_MS);
  };

  const onCopy = async () => {
    try {
      await copySmartFromRendered(message.content, renderedRootRef.current);
      flashCopied();
    } catch {
      try {
        await copyMarkdown(message.content || '');
        flashCopied();
      } catch {
        /* clipboard denied or unsupported */
      }
    }
  };

  const waitingForFirstToken =
    message.streaming && !String(message.content || '').trim();

  const thinkingLong = useThinkingEscalation(
    waitingForFirstToken && !emptyStreamingHint,
  );

  const thinkingLabel = emptyStreamingHint
    ? emptyStreamingHint
    : thinkingLong
    ? 'Thinking longer for a better answer…'
    : 'Thinking…';

  return (
    <div className="ddv-ai-chat__msg ddv-ai-chat__msg--assistant">
      <div className="ddv-ai-chat__bubble-assistant">
        <div className="ddv-ai-chat__bubble-assistant-inner">
          <div
            ref={renderedRootRef}
            className="ddv-ai-chat__markdown-copy-root"
          >
            {waitingForFirstToken ? (
              <div className="ddv-ai-chat__thinking-line">
                <Spin size="small" className="ddv-ai-chat__spin-thinking" />
                <span>{thinkingLabel}</span>
              </div>
            ) : (
              <MarkdownContent text={message.content} />
            )}
          </div>
        </div>
      </div>
      {showCopy && canCopy ? (
        <div
          className={
            dimCopyUntilHover
              ? 'ddv-ai-chat__bubble-actions ddv-ai-chat__bubble-actions--hover-reveal'
              : 'ddv-ai-chat__bubble-actions'
          }
        >
          <AiChatTooltip title={copied ? 'Copied!' : 'Copy'}>
            <button
              type="button"
              className="ddv-ai-chat__bubble-action-btn"
              onClick={onCopy}
              aria-label={copied ? 'Copied' : 'Copy message'}
            >
              {copied ? (
                <CheckOutlined aria-hidden />
              ) : (
                <CopyOutlineIcon className="ddv-ai-chat__bubble-appkit-icon" />
              )}
            </button>
          </AiChatTooltip>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {{ chatbotModuleId?: string | null }} props
 */
export default function AiChatShell({ chatbotModuleId = null }) {
  const projectId = useProjectId();
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const userFirstName = useSelector((state) =>
    typeof state.login?.userInfo?.given_name === 'string'
      ? state.login.userInfo.given_name.trim()
      : '',
  );
  const {
    messages,
    loading,
    streaming,
    error,
    sendMessage,
    resetChat,
    dismissError,
  } = useAiChat();
  const [expanded, setExpanded] = useState(false);
  const [layoutMode, setLayoutMode] = useState(
    /** @type {'docked' | 'moduleFull'} */ ('docked'),
  );
  const [panelView, setPanelView] = useState('chat');
  const [draft, setDraft] = useState('');
  const [markdownPreviewOpen, setMarkdownPreviewOpen] = useState(false);
  const [welcomeAgentDisplay, setWelcomeAgentDisplay] = useState(null);
  const [welcomeIntroTemplate, setWelcomeIntroTemplate] = useState('');
  const [welcomeConfigNonce, setWelcomeConfigNonce] = useState(0);
  const [welcomeMetaStatus, setWelcomeMetaStatus] = useState(
    /** @type {'idle' | 'loading' | 'done'} */ ('idle'),
  );
  const [panelWidthPx, setPanelWidthPx] = useState(() => {
    const stored = readStoredPanelWidthPx(projectId);
    if (stored != null) {
      return stored;
    }
    return clampPanelWidthToViewport(DEFAULT_PANEL_WIDTH_PX);
  });
  const listRef = useRef(null);

  const draftLooksLikeMarkdown = useMemo(
    () => looksLikeMarkdown(draft),
    [draft],
  );

  const lastAssistantMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (!draft.trim()) {
      setMarkdownPreviewOpen(false);
      return;
    }
    if (!looksLikeMarkdown(draft)) {
      setMarkdownPreviewOpen(false);
    }
  }, [draft]);

  const busy = loading || streaming;

  useEffect(() => {
    const id = projectId && String(projectId).trim();
    if (!id) {
      return;
    }
    const stored = readStoredPanelWidthPx(id);
    if (stored != null) {
      setPanelWidthPx(stored);
    }
  }, [projectId]);

  const onNewChat = () => {
    if (panelView === 'config') {
      setPanelView('chat');
    }
    resetChat();
    setDraft('');
    setMarkdownPreviewOpen(false);
    requestAnimationFrame(() => {
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const onPanelResizePointerDown = useCallback(
    (e) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      const el = e.currentTarget;
      const pointerId = e.pointerId;
      const startX = e.clientX;
      const startW = panelWidthPx;
      el.setPointerCapture(pointerId);
      const onMove = (ev) => {
        if (ev.pointerId !== pointerId) {
          return;
        }
        const next = clampPanelWidthToViewport(startW - (ev.clientX - startX));
        syncDockedChatLayoutDom(next);
      };
      const onUp = (ev) => {
        if (ev.pointerId !== pointerId) {
          return;
        }
        el.releasePointerCapture(pointerId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        const final = syncDockedChatLayoutDom(startW - (ev.clientX - startX));
        requestAnimationFrame(() => {
          setPanelWidthPx(final);
          writeStoredPanelWidthPx(projectIdRef.current, final);
        });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [panelWidthPx],
  );

  const onPanelResizeDoubleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const w = clampPanelWidthToViewport(DEFAULT_PANEL_WIDTH_PX);
    setPanelWidthPx(w);
    writeStoredPanelWidthPx(projectIdRef.current, w);
  }, []);

  useEffect(() => {
    let rafId = null;
    const clearLayoutHook = () => {
      document.documentElement.removeAttribute('data-ai-chat-open');
      document.documentElement.removeAttribute('data-ai-chat-module-full');
      document.documentElement.removeAttribute('data-ai-chat-docked-layout');
      document.documentElement.style.removeProperty(
        '--ddv-ai-chat-panel-width',
      );
    };

    if (expanded) {
      rafId = requestAnimationFrame(() => {
        document.documentElement.setAttribute('data-ai-chat-open', 'true');
        if (layoutMode === 'docked') {
          document.documentElement.style.setProperty(
            '--ddv-ai-chat-panel-width',
            `${panelWidthPx}px`,
          );
          document.documentElement.setAttribute(
            'data-ai-chat-docked-layout',
            panelWidthPx > CHAT_SHRINK_LAYOUT_MAX_WIDTH_PX
              ? 'overlay'
              : 'shrink',
          );
        } else {
          document.documentElement.style.removeProperty(
            '--ddv-ai-chat-panel-width',
          );
          document.documentElement.removeAttribute(
            'data-ai-chat-docked-layout',
          );
        }
        if (layoutMode === 'moduleFull') {
          document.documentElement.setAttribute(
            'data-ai-chat-module-full',
            'true',
          );
        } else {
          document.documentElement.removeAttribute('data-ai-chat-module-full');
        }
      });
    } else {
      clearLayoutHook();
    }

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      clearLayoutHook();
    };
  }, [expanded, layoutMode, panelWidthPx]);

  useEffect(() => {
    if (typeof window === 'undefined' || !expanded || layoutMode !== 'docked') {
      return undefined;
    }
    const onResize = () => {
      setPanelWidthPx((prev) => {
        const next = clampPanelWidthToViewport(prev);
        if (next !== prev) {
          writeStoredPanelWidthPx(projectIdRef.current, next);
        }
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [expanded, layoutMode]);

  useEffect(() => {
    if (!expanded) {
      setPanelView('chat');
      setLayoutMode('docked');
      setMarkdownPreviewOpen(false);
    }
  }, [expanded]);

  useEffect(() => {
    const localIntro =
      projectId && String(projectId).trim()
        ? getConnectionSettings(projectId).introMessage || ''
        : '';
    if (!chatbotModuleId || !getAiChatApiBase()) {
      setWelcomeAgentDisplay(null);
      setWelcomeIntroTemplate(localIntro);
      setWelcomeMetaStatus('done');
      return undefined;
    }

    const moduleKey = String(chatbotModuleId).trim();
    let cancelled = false;
    setWelcomeMetaStatus('loading');

    fetchChatbotConfig(moduleKey)
      .then((cfg) => {
        if (cancelled) {
          return;
        }
        setWelcomeAgentDisplay(resolveWelcomeAgentDisplayName(cfg));
        const hasIntroMessageField =
          cfg &&
          typeof cfg === 'object' &&
          Object.prototype.hasOwnProperty.call(cfg, 'introMessage');
        if (hasIntroMessageField) {
          setWelcomeIntroTemplate(
            typeof cfg?.introMessage === 'string' ? cfg.introMessage : '',
          );
          return;
        }
        setWelcomeIntroTemplate(localIntro);
      })
      .catch(() => {
        if (!cancelled) {
          setWelcomeAgentDisplay(null);
          setWelcomeIntroTemplate(localIntro);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setWelcomeMetaStatus('done');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chatbotModuleId, welcomeConfigNonce, projectId]);

  useEffect(() => {
    const el = listRef.current;
    if (el && panelView === 'chat') {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [messages, loading, streaming, expanded, panelView]);

  const onSubmit = () => {
    sendMessage(draft);
    setDraft('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const needWelcomeConfigFetch = Boolean(chatbotModuleId && getAiChatApiBase());
  const welcomeIntroLoading =
    needWelcomeConfigFetch &&
    (welcomeMetaStatus === 'idle' || welcomeMetaStatus === 'loading');

  const welcomeBubbleMarkdown = useMemo(() => {
    const template = (welcomeIntroTemplate || '').trim();
    if (template) {
      return applyIntroMessagePlaceholders(template, {
        userFirstName: escapeMarkdownAsterisks(userFirstName),
        agentName: escapeMarkdownAsterisks(welcomeAgentDisplay || ''),
      });
    }
    if (!welcomeAgentDisplay) {
      return null;
    }
    const agent = escapeMarkdownAsterisks(welcomeAgentDisplay);
    if (userFirstName) {
      const first = escapeMarkdownAsterisks(userFirstName);
      return `Hi **${first}**, I'm **${agent}**, your chat assistant for this module. Ask a question.`;
    }
    return `Hi, I'm **${agent}**, your chat assistant for this module. Ask a question.`;
  }, [welcomeIntroTemplate, welcomeAgentDisplay, userFirstName]);
  const hasWelcomeBubbleMarkdown = Boolean(
    welcomeBubbleMarkdown && String(welcomeBubbleMarkdown).trim(),
  );
  const showWelcomeLoadingBubble =
    welcomeIntroLoading && !hasWelcomeBubbleMarkdown;
  const welcomeBubbleContent = hasWelcomeBubbleMarkdown
    ? welcomeBubbleMarkdown
    : 'Ask a question in natural language. Answers support **Markdown**.';
  const headerTitle = welcomeAgentDisplay || 'AI Assistant';

  const panelClassName =
    layoutMode === 'moduleFull'
      ? 'ddv-ai-chat__panel ddv-ai-chat__panel--module-full'
      : 'ddv-ai-chat__panel';

  return (
    <div className="ddv-ai-chat" aria-label="AI assistant chat">
      {!expanded ? (
        <button
          type="button"
          className="ddv-ai-chat__fab"
          onClick={() => {
            requestAnimationFrame(() => {
              setExpanded(true);
            });
          }}
          aria-expanded={false}
          aria-controls="ddv-ai-chat-panel"
          aria-label={`Open ${headerTitle}`}
        >
          <BotFillIcon className="ddv-ai-chat__fab-icon" />
          <span className="ddv-ai-chat__fab-label">{headerTitle}</span>
        </button>
      ) : (
        <div
          id="ddv-ai-chat-panel"
          className={panelClassName}
          role="dialog"
          aria-modal="false"
          aria-label="AI Assistant chat panel"
        >
          {layoutMode === 'docked' ? (
            <div
              className="ddv-ai-chat__panel-resize-handle"
              onPointerDown={onPanelResizePointerDown}
              onDoubleClick={onPanelResizeDoubleClick}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize chat panel"
              aria-valuemin={MIN_PANEL_WIDTH_PX}
              aria-valuemax={MAX_PANEL_WIDTH_PX}
              aria-valuenow={panelWidthPx}
            />
          ) : null}
          <header className="ddv-ai-chat__header">
            <div className="ddv-ai-chat__header-brand">
              <span className="ddv-ai-chat__brand-mark" aria-hidden>
                <BotFillIcon className="ddv-ai-chat__brand-mark-icon" />
              </span>
              <div className="ddv-ai-chat__header-text">
                <div className="ddv-ai-chat__title-row">
                  <span className="ddv-ai-chat__title">{headerTitle}</span>
                  {showAiChatBetaChip() ? (
                    <span className="ddv-ai-chat__beta-chip">BETA</span>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="ddv-ai-chat__header-actions">
              <div className="ddv-ai-chat__header-actions-chrome">
                <AiChatTooltip title="New chat">
                  <button
                    type="button"
                    className="ddv-ai-chat__header-icon-btn"
                    onClick={onNewChat}
                    aria-label="New chat"
                  >
                    <OpenInNewOutlineIcon className="ddv-ai-chat__header-appkit-icon" />
                  </button>
                </AiChatTooltip>
                <AiChatTooltip
                  title={layoutMode === 'moduleFull' ? 'Collapse' : 'Expand'}
                >
                  <button
                    type="button"
                    className="ddv-ai-chat__header-icon-btn"
                    onClick={() =>
                      setLayoutMode((m) =>
                        m === 'docked' ? 'moduleFull' : 'docked',
                      )
                    }
                    aria-label={
                      layoutMode === 'moduleFull'
                        ? 'Dock chat to narrow panel'
                        : 'Expand chat to full module width'
                    }
                    aria-pressed={layoutMode === 'moduleFull'}
                  >
                    {layoutMode === 'moduleFull' ? (
                      <MinimizeOutlineIcon className="ddv-ai-chat__header-appkit-icon" />
                    ) : (
                      <ExpandOutlineIcon className="ddv-ai-chat__header-appkit-icon" />
                    )}
                  </button>
                </AiChatTooltip>
                <AiChatTooltip title="Configure">
                  <button
                    type="button"
                    className={
                      panelView === 'config'
                        ? 'ddv-ai-chat__header-icon-btn ddv-ai-chat__header-icon-btn--active'
                        : 'ddv-ai-chat__header-icon-btn'
                    }
                    onClick={() =>
                      setPanelView((v) => (v === 'config' ? 'chat' : 'config'))
                    }
                    aria-label="Chat configuration"
                    aria-pressed={panelView === 'config'}
                  >
                    <SettingOutlineIcon className="ddv-ai-chat__header-appkit-icon" />
                  </button>
                </AiChatTooltip>
                <AiChatTooltip title="Close">
                  <button
                    type="button"
                    className="ddv-ai-chat__header-icon-btn"
                    onClick={() => setExpanded(false)}
                    aria-label="Close chat"
                  >
                    <CloseOutlineIcon className="ddv-ai-chat__header-appkit-icon" />
                  </button>
                </AiChatTooltip>
              </div>
            </div>
          </header>

          {panelView === 'config' ? (
            <AiChatConfigPane
              projectId={projectId}
              moduleId={chatbotModuleId}
              onClose={() => setPanelView('chat')}
              onSaved={(payload) => {
                setWelcomeIntroTemplate(payload?.introMessage ?? '');
                setWelcomeConfigNonce((n) => n + 1);
              }}
            />
          ) : null}

          <div
            className="ddv-ai-chat__body"
            ref={listRef}
            hidden={panelView === 'config'}
          >
            {error ? (
              <Alert
                type="error"
                showIcon
                message="Could not get a response"
                description={error}
                closable
                onClose={dismissError}
                className="ddv-ai-chat__alert"
              />
            ) : null}

            <AssistantMessageBubble
              key="welcome"
              showCopy={false}
              message={{
                id: 'welcome',
                content: showWelcomeLoadingBubble ? '' : welcomeBubbleContent,
                streaming: showWelcomeLoadingBubble,
              }}
              emptyStreamingHint={
                showWelcomeLoadingBubble
                  ? 'Loading assistant information…'
                  : undefined
              }
            />

            {messages.map((m) =>
              m.role === 'assistant' ? (
                <AssistantMessageBubble
                  key={m.id}
                  message={m}
                  dimCopyUntilHover={m.id !== lastAssistantMessageId}
                />
              ) : (
                <div
                  key={m.id}
                  className="ddv-ai-chat__msg ddv-ai-chat__msg--user"
                >
                  <div className="ddv-ai-chat__bubble-user">
                    <MarkdownContent text={m.content} />
                  </div>
                </div>
              ),
            )}

            {loading ? (
              <div className="ddv-ai-chat__msg ddv-ai-chat__msg--assistant">
                <div className="ddv-ai-chat__bubble-assistant">
                  <div className="ddv-ai-chat__thinking-line">
                    <Spin size="small" className="ddv-ai-chat__spin-thinking" />
                    <span>Thinking…</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <footer
            className="ddv-ai-chat__footer"
            hidden={panelView === 'config'}
          >
            <div className="ddv-ai-chat__composer-wrap">
              <div className="ddv-ai-chat__composer">
                <Input.TextArea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Write a message..."
                  rows={3}
                  disabled={busy}
                  bordered={false}
                  className="ddv-ai-chat__textarea"
                  aria-label="Message to AI assistant"
                />
                {!busy &&
                draft.trim() &&
                draftLooksLikeMarkdown &&
                !markdownPreviewOpen ? (
                  <button
                    type="button"
                    className="ddv-ai-chat__composer-md-preview-fab"
                    onClick={() => setMarkdownPreviewOpen(true)}
                    aria-expanded={false}
                    aria-controls="ddv-ai-chat-composer-md-preview"
                  >
                    <EyeOutlined aria-hidden />
                    <span>Preview</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ddv-ai-chat__send-btn"
                  onClick={onSubmit}
                  disabled={busy || !draft.trim()}
                  aria-label="Send message"
                >
                  <ArrowRightOutlined />
                </button>
              </div>
              {markdownPreviewOpen && draft.trim() ? (
                <div
                  id="ddv-ai-chat-composer-md-preview"
                  className="ddv-ai-chat__composer-preview"
                  aria-label="Formatted preview of your message"
                >
                  <div className="ddv-ai-chat__composer-preview-bar">
                    <span className="ddv-ai-chat__composer-preview-title">
                      Preview
                    </span>
                    <button
                      type="button"
                      className="ddv-ai-chat__composer-preview-hide"
                      onClick={() => setMarkdownPreviewOpen(false)}
                    >
                      Hide preview
                    </button>
                  </div>
                  <MarkdownContent text={draft} />
                </div>
              ) : null}
            </div>
            <p className="ddv-ai-chat__disclaimer">
              AI responses are generated and may not always be accurate. Verify
              important information.
            </p>
          </footer>
        </div>
      )}
    </div>
  );
}
